import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";

import { Typography, useTheme } from "@mui/material";

import { useClickOutside } from "hooks/click-outside";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import Search from "icons/Search";
import ChevronDown from "icons/ChevronDown";

import { Checkbox } from "../Checkbox";
import { Button } from "components/common/Button";
import { Input } from "../Input";
import {
  StyledSelect,
  StyledSelectAddAllField,
  StyledSelectAddNewOptionBtnField,
  StyledSelectAddNewOptionField,
  StyledSelectArrow,
  StyledSelectContainer,
  StyledSelectContent,
  StyledSelectEmptyData,
  StyledSelectError,
  StyledSelectExtraField,
  StyledSelectField,
  StyledSelectItem,
  StyledSelectLabel,
  StyledSelectList,
  StyledSelectTitle,
} from "styles/components/inputs/Select";
import { Close } from "@mui/icons-material";
import Stack from "@mui/material/Stack";

type SelectOption = {
  value: string;
  label: string;
};

export interface SelectProps {
  id?: string;
  options?: SelectOption[];
  value?: string[];
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  errorText?: string;
  dataTestId?: string;
  color?: "white" | "grey";
  withAddAll?: boolean;
  withSearch?: boolean;
  withAddNewOption?: boolean;
  searchPlaceholder?: string;
  addNewOptionPlaceholder?: string;
  addNewOptionBtnText?: string;
  withClose?: boolean;

  onChange?: (value: string[]) => void;
}

const initialSelectOptions: SelectOption[] = [
  { value: "first", label: "First item" },
  { value: "second", label: "Second item" },
];

const Multiselect: FC<SelectProps> = ({
  id,
  options = initialSelectOptions,
  label = "",
  value,
  required,
  dataTestId,
  className,
  placeholder = "Placeholder",
  searchPlaceholder = "Search...",
  addNewOptionPlaceholder = "Add a new option",
  addNewOptionBtnText = "Add a new option",
  color,
  error,
  errorText = "Error text",
  disabled = false,
  withAddAll = false,
  withSearch = false,
  withAddNewOption = false,
  onChange,
  withClose,
}) => {
  const theme = useTheme();

  const [actualOptions, setActualOptions] = useState<SelectOption[]>(
    options ?? []
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(value ?? []);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [newOptionName, setNewOptionName] = useState<string>("");
  const [newOptionFieldIsVisible, setNewOptionFieldIsVisible] =
    useState<boolean>(false);
  const [newOptionNameError, setNewOptionNameError] = useState<boolean>(false);

  useEffect(() => {
    if (options && actualOptions.length === 0) {
      setActualOptions(options);
    }
  }, [options, actualOptions]);

  useEffect(() => {
    if (value) {
      setSelectedOptions(value);
    }
  }, [value]);

  const selectRef = useRef<HTMLDivElement>(null);

  const wrapClassName = clsx(["select-container", className]);

  const selectClassName = clsx([
    "select",
    {
      select_open: isOpen && !disabled,
      select_grey: color === "grey",
      select_white: color === "white",
      select_error: error,
      select_disabled: disabled,
    },
  ]);

  const errorClassName = clsx("select-error", {
    "select-error_visible": error,
  });

  const withExtraField = useMemo(
    () => !!withAddAll || !!withSearch || !!withAddNewOption,
    [withAddAll, withAddNewOption, withSearch]
  );

  const renderedLabel = useMemo(
    () =>
      label ? (
        <StyledSelectLabel
          htmlFor={id}
          data-testid={dataTestId && `${dataTestId}-label`}
        >
          {label}
          {required ? <span className="select__marker">*</span> : null}
        </StyledSelectLabel>
      ) : null,
    [dataTestId, id, label, required]
  );

  const chosenTitle = useMemo(
    () =>
      actualOptions
        ?.filter(({ value }) => selectedOptions.includes(value))
        .map(({ label }) => label)
        .join(", "),
    [actualOptions, selectedOptions]
  );

  const handleSelectClose = useCallback(() => {
    setIsOpen(false);

    setSearchedValue("");

    setNewOptionFieldIsVisible(false);

    setNewOptionName("");

    setNewOptionNameError(false);

    onChange?.(selectedOptions);
  }, [onChange, selectedOptions]);

  const handleToggleSelect = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      setIsOpen((prev) => !prev);

      if (isOpen) {
        handleSelectClose();
      }
    },
    [handleSelectClose, isOpen]
  );

  useClickOutside({
    ref: selectRef,
    onClickOutside: handleSelectClose,
    closeOnEsc: isOpen,
    closeOnOutside: true,
    isOpen: isOpen,
  });

  const handleSelectChange = useCallback(
    (value: string) => (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      setSelectedOptions((prev) =>
        prev.includes(value)
          ? prev.filter((option) => option !== value)
          : [...prev, value]
      );
    },
    []
  );

  const handleSelectCheckboxChange = useCallback(
    (value: string) => () => {
      setSelectedOptions((prev) =>
        prev.includes(value)
          ? prev.filter((option) => option !== value)
          : [...prev, value]
      );
    },
    []
  );

  const showNewOptionField = useCallback(
    () => setNewOptionFieldIsVisible(true),
    []
  );

  const handleSelectAll = useCallback(() => {
    setSelectedOptions(actualOptions.map(({ value }) => value));
  }, [actualOptions]);

  const handleSearch = useCallback(
    (value: string) => setSearchedValue(value),
    []
  );

  const handleNewOptionNameChange = useCallback((value: string) => {
    setNewOptionNameError(false);
    setNewOptionName(value);
  }, []);

  const handleNewOptionAdd = useCallback(() => {
    if (newOptionName) {
      const newOption = {
        label: newOptionName,
        value: newOptionName,
      };

      const duplicate = actualOptions.find(
        ({ value }) =>
          value.toLocaleLowerCase() === newOptionName.toLocaleLowerCase()
      );

      if (duplicate) {
        setNewOptionNameError(true);
      } else {
        setActualOptions((prev) => [...prev, newOption]);

        setSelectedOptions((prev) => [
          ...prev,
          newOptionName.toLocaleLowerCase(),
        ]);

        handleSelectClose();
      }

      setNewOptionName("");
    }
  }, [actualOptions, handleSelectClose, newOptionName]);

  const renderedAddAllField = useMemo(
    () =>
      withAddAll ? (
        <StyledSelectAddAllField>
          <Button variant="text" underlined onClick={handleSelectAll}>
            Add all
          </Button>
        </StyledSelectAddAllField>
      ) : null,
    [handleSelectAll, withAddAll]
  );

  const renderedSearchField = useMemo(
    () =>
      withSearch ? (
        <Input
          prefix={<Search />}
          label=""
          withErrorField={false}
          placeholder={searchPlaceholder}
          inputClassName="select-search"
          onChange={handleSearch}
          value={searchedValue}
        />
      ) : null,
    [handleSearch, searchPlaceholder, searchedValue, withSearch]
  );

  const filteredOptions = useMemo(
    () =>
      searchedValue && searchedValue.length > 0
        ? actualOptions.filter(({ value }) =>
            value
              .substring(0, searchedValue.length)
              .toLocaleLowerCase()
              .includes(searchedValue.toLocaleLowerCase())
          )
        : actualOptions,
    [actualOptions, searchedValue]
  );

  const renderedAddNewOptionBtnField = useMemo(
    () =>
      withAddNewOption ? (
        <StyledSelectAddNewOptionBtnField>
          <Button
            variant="text"
            startIcon={<AddOutlinedIcon />}
            onClick={showNewOptionField}
          >
            {addNewOptionBtnText}
          </Button>
        </StyledSelectAddNewOptionBtnField>
      ) : null,
    [addNewOptionBtnText, showNewOptionField, withAddNewOption]
  );

  const renderedAddNewOptionField = useMemo(
    () =>
      newOptionFieldIsVisible ? (
        <StyledSelectAddNewOptionField>
          <Input
            label=""
            withErrorField={false}
            placeholder={addNewOptionPlaceholder}
            inputClassName="select-new-option"
            onChange={handleNewOptionNameChange}
            value={newOptionName}
            autoFocus
            error={newOptionNameError}
          />
          <Button
            variant="text"
            onClick={handleNewOptionAdd}
            underlined
            disabled={!newOptionName}
          >
            Save
          </Button>
        </StyledSelectAddNewOptionField>
      ) : null,
    [
      addNewOptionPlaceholder,
      handleNewOptionAdd,
      handleNewOptionNameChange,
      newOptionFieldIsVisible,
      newOptionName,
      newOptionNameError,
    ]
  );

  const renderedExtraField = useMemo(
    () =>
      withExtraField ? (
        <StyledSelectExtraField grey={color === "grey" ? 1 : 0}>
          {renderedAddNewOptionBtnField}
          {renderedAddNewOptionField}
          {renderedSearchField}
          {renderedAddAllField}
        </StyledSelectExtraField>
      ) : null,
    [
      color,
      renderedAddAllField,
      renderedAddNewOptionBtnField,
      renderedAddNewOptionField,
      renderedSearchField,
      withExtraField,
    ]
  );

  const renderedClose = useMemo(
    () =>
      withClose ? (
        <Stack direction={"row"} justifyContent={"flex-end"}>
          <Button variant="text" onClick={handleSelectClose} underlined>
            <Close />
          </Button>
        </Stack>
      ) : null,
    [withClose, handleSelectClose]
  );

  return (
    <StyledSelectContainer className={wrapClassName} data-testid={dataTestId}>
      {renderedLabel}
      <StyledSelectField ref={selectRef}>
        <StyledSelect
          className={selectClassName}
          onClick={handleToggleSelect}
          id={id}
          data-testid={dataTestId && `${dataTestId}-select`}
        >
          <StyledSelectTitle
            variant="bodyL"
            color={
              disabled || !chosenTitle
                ? theme.palette.customGrey.main
                : theme.palette.common.black
            }
          >
            {chosenTitle || placeholder}
          </StyledSelectTitle>
          <StyledSelectArrow className="select__arrow">
            <ChevronDown />
          </StyledSelectArrow>
        </StyledSelect>
        <StyledSelectContent className="select__content">
          {renderedClose}
          {renderedExtraField}
          <StyledSelectList className="select__list">
            {filteredOptions.length > 0 ? (
              filteredOptions?.map(({ value, label }) => (
                <StyledSelectItem
                  onClick={handleSelectChange(value)}
                  key={value}
                  grey={color === "grey" ? 1 : 0}
                  className={clsx("select__option", {
                    select__option_active: selectedOptions.some(
                      (option) => option === value
                    ),
                  })}
                >
                  <Checkbox
                    checked={selectedOptions.some((option) => option === value)}
                    onChange={handleSelectCheckboxChange(value)}
                  />
                  {label}
                </StyledSelectItem>
              ))
            ) : (
              <StyledSelectEmptyData>
                <Typography
                  variant="bodyM"
                  color={theme.palette.customGrey.main}
                >
                  No data
                </Typography>
              </StyledSelectEmptyData>
            )}
          </StyledSelectList>
        </StyledSelectContent>
      </StyledSelectField>
      <StyledSelectError
        variant="bodyM"
        fontWeight={300}
        className={errorClassName}
        data-testid={dataTestId && `${dataTestId}-error`}
      >
        {errorText}
      </StyledSelectError>
    </StyledSelectContainer>
  );
};

export default Multiselect;
